import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SFChipList from "../../chip/chipList";
import SFPopover from "../../popover";
import { SFStack } from "../../grid";
import SFChip from "../../chip";
import _ from "lodash";
import { GRID_FILTERS } from "../dataGridConfig";
import SFTypography from "../../typography";
import getStyled from "../../../hooks/getStyled";
import { SFCloseIcon } from "../../icons";
import ActionBtns from "./actionBtns";
import { getMonthDayYearDate } from "../../../services/dateService";

const { styled } = getStyled();

const SFPopoverStyled = styled(SFPopover)(() => ({
  "& .MuiPopover-paper": {
    boxShadow: "none",
  },
}));

const FilterChips = ({
  gridName,
  onClearFilters,
  columnDefs,
  getAcceptableFilters,
  onEditFilters,
  readOnly = false,
}) => {
  const savedLayout = useSelector((state) => {
    return state.gridLayout.find(
      (gridLayout) => gridLayout.gridName === gridName
    );
  });
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [popoverItems, setPopoverItems] = useState([]);

  let filterModel = savedLayout
    ? getAcceptableFilters
      ? getAcceptableFilters(savedLayout.filterModel)
      : savedLayout.filterModel
    : {};

  //Handle container resize; must update width in order to show correct the collapsedChipsBtn
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(containerRef.current.offsetWidth - 8);
    };

    handleResize();

     
    window.addEventListener("resize", handleResize);

    return () => {
       
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { t } = useTranslation();

  const BLANK_CHIP_LENGTH = t("components.datagrid.filters.blankValue").length;

  const expandFilterValues = (e, filterKey, groupKey = null) => {
    let items = [];
    //if it's a groupTreeFilter
    if (groupKey) {
      let filter = filterModel[groupKey].columns[filterKey];
      items = filter.values.map((x) => ({
        key: filterKey,
        value: x.value,
        filterType: filterModel[groupKey].filterType,
        groupKey: groupKey,
        id: x.id,
      }));
    } else if (
      filterModel[filterKey].filterType === GRID_FILTERS.filterType.number
    ) {
      items.push({
        key: filterKey,
        value: getIntFilterVal(filterModel[filterKey]),
        filterType: filterModel[filterKey].filterType,
      });
    } else if (
      filterModel[filterKey].filterType === GRID_FILTERS.filterType.date
    ) {
      items.push({
        key: filterKey,
        value: getDateFilterVal(filterModel[filterKey]),
        filterType: filterModel[filterKey].filterType,
      });
    } else {
      let filter = filterModel[filterKey];
      items = filter.values.map((x) => ({
        key: filterKey,
        value: x,
        filterType: filter.filterType,
        displayValue: transformChipValue(filterModel[filterKey].displayName, x),
      }));
    }

    setPopoverItems(items);
    setAnchorEl(e.target.offsetParent);
  };

  const removeFilter = (key, value, filterType, groupKey = null, id = null) => {
    const filtersValues = JSON.parse(JSON.stringify(savedLayout.filterModel));
    let expandedItems = popoverItems;
    if (filterType === GRID_FILTERS.filterType.set) {
      const newFilterVals = filtersValues[key].values.filter(
        (val) => val !== value
      );
      filtersValues[key].values = newFilterVals;
      expandedItems = popoverItems.filter(
        (x) => !(x.value === value && x.key === key)
      );
    } else if (filterType === GRID_FILTERS.filterType.groupTreeFilter) {
      filtersValues[groupKey].columns[key].values = filtersValues[
        groupKey
      ].columns[key].values.filter((val) => val.id !== id);
      if (filtersValues[groupKey].columns[key].values.length === 0) {
        delete filtersValues[groupKey].columns[key];
      }
      if (_.isEmpty(filtersValues[groupKey].columns)) {
        delete filtersValues[groupKey];
      }
      expandedItems = popoverItems.filter(
        (x) => !(x.id === id && x.key === key)
      );
    } else {
      delete filtersValues[key];
      expandedItems = popoverItems.filter(
        (x) => !(x.value === value && x.key === key)
      );
    }

    if (expandedItems.length === 0) {
      setAnchorEl(null);
    }

    setPopoverItems(expandedItems);

    onClearFilters(filtersValues);
  };

  const tranformIntChipToText = (type, filter, filterTo = null) => {
    let value;
    switch (type) {
    case GRID_FILTERS.type.inRange:
      value = t("components.datagrid.filters.inRange", {
        val1: filter,
        val2: filterTo,
      });
      break;
    case GRID_FILTERS.type.equals:
      value = t("components.datagrid.filters.equals", {
        val: filter,
      });
      break;
    case GRID_FILTERS.type.notEqual:
      value = t("components.datagrid.filters.notEqual", {
        val: filter,
      });
      break;
    case GRID_FILTERS.type.lessThanOrEqual:
      value = t("components.datagrid.filters.lessThanOrEqual", {
        val: filter,
      });
      break;
    case GRID_FILTERS.type.lessThan:
      value = t("components.datagrid.filters.lessThan", {
        val: filter,
      });
      break;
    case GRID_FILTERS.type.greaterThanOrEqual:
      value = t("components.datagrid.filters.greaterThanOrEqual", {
        val: filter,
      });
      break;
    case GRID_FILTERS.type.greaterThan:
      value = t("components.datagrid.filters.greaterThan", {
        val: filter,
      });
      break;
    case GRID_FILTERS.type.blank:
      value = t("components.datagrid.filters.blank");
      break;
    case GRID_FILTERS.type.notBlank:
    default:
      value = t("components.datagrid.filters.notBlank");
      break;
    }
    return value;
  };

  const transformDateChipToText = (type, dateFrom, dateTo) => {
    const formattedDateFrom = getMonthDayYearDate(dateFrom);
    const formattedDateTo = getMonthDayYearDate(dateTo);
    let value;
    switch (type) {
    case GRID_FILTERS.type.inRange:
      value = t("components.datagrid.filters.inRange", {
        val1: formattedDateFrom,
        val2: formattedDateTo,
      });
      break;
    case GRID_FILTERS.type.greaterThan:
      value = t("components.datagrid.filters.greaterThan", {
        val: formattedDateFrom,
      });
      break;
    case GRID_FILTERS.type.lessThan:
      value = t("components.datagrid.filters.lessThan", {
        val: formattedDateFrom,
      });
      break;
    default:
      value = t("components.datagrid.filters.notBlank");
      break;
    }
    return value;
  };

  const getIntFilterVal = (filterObj) => {
    if (filterObj.operator && filterObj.condition1 && filterObj.condition2) {
      return `${tranformIntChipToText(
        filterObj.condition1.type,
        filterObj.condition1.filter,
        filterObj.condition1?.filterTo
      )} ${
        filterObj.operator === GRID_FILTERS.operator.and
          ? t("components.datagrid.filters.operators.and")
          : t("components.datagrid.filters.operators.or")
      } ${tranformIntChipToText(
        filterObj.condition2.type,
        filterObj.condition2.filter,
        filterObj.condition2?.filterTo
      )}`;
    } else {
      return tranformIntChipToText(
        filterObj.type,
        filterObj.filter,
        filterObj?.filterTo
      );
    }
  };

  const getDateFilterVal = (filterObj) => {
    return transformDateChipToText(
      filterObj.type,
      filterObj.dateFrom,
      filterObj.dateTo
    );
  };

  //calculate width of next filter in filterModel  if exists
  const getChipWidthOfNextFilter = (curFilterKey, groupKey = null) => {
    let width = 0;
    //check if is a groupTreeFilter
    if (groupKey) {
      //check if specific group has another filter
      let filterModelKeys = Object.keys(filterModel[groupKey].columns);
      let nextIndex = filterModelKeys.indexOf(curFilterKey) + 1;
      if (nextIndex < filterModelKeys.length) {
        let chipValue;
        let nextFilterKey = filterModelKeys[nextIndex];
        let nextFilterKeyValues =
          filterModel[groupKey].columns[nextFilterKey].filterType ===
          GRID_FILTERS.filterType.number
            ? [getIntFilterVal(filterModel[groupKey].columns[nextFilterKey])]
            : filterModel[groupKey].columns[nextFilterKey].values;
        if (nextFilterKeyValues.length > 1) {
          chipValue = nextFilterKeyValues.length;
        } else {
          chipValue = nextFilterKeyValues[0].value;
        }
        width =
          (filterModel[groupKey].displayName.length +
            (chipValue?.toString().length ?? BLANK_CHIP_LENGTH)) *
          8.8;
      } else {
        //check there's another existing filter key
        let filterModelKeys = Object.keys(filterModel);
        let nextIndex = filterModelKeys.indexOf(groupKey) + 1;
        if (nextIndex < filterModelKeys.length) {
          let chipValue;
          let nextFilterKey = filterModelKeys[nextIndex];
          let nextFilterKeyValues = null;
          switch (filterModel[nextFilterKey].filterType) {
          case GRID_FILTERS.filterType.number:
            nextFilterKeyValues = [
              getIntFilterVal(filterModel[nextFilterKey]),
            ];
            break;
          case GRID_FILTERS.filterType.date:
            nextFilterKeyValues = [
              transformDateChipToText(filterModel[nextFilterKey]),
            ];
            break;
          case GRID_FILTERS.filterType.groupTreeFilter:
            nextFilterKeyValues =
                filterModel[nextFilterKey].columns[
                  Object.keys(filterModel[nextFilterKey].columns)[0]
                ].values;
            break;
          case GRID_FILTERS.filterType.set:
          default:
            nextFilterKeyValues = filterModel[nextFilterKey].values;
            break;
          }
          if (nextFilterKeyValues.length > 1) {
            chipValue = nextFilterKeyValues.length;
          } else {
            chipValue =
              filterModel[nextFilterKey].filterType ===
              GRID_FILTERS.filterType.groupTreeFilter
                ? nextFilterKeyValues[0].value
                : nextFilterKeyValues[0];
          }
          //approx. width of filter
          width =
            (filterModel[nextFilterKey].displayName.length +
              (chipValue?.toString().length ?? BLANK_CHIP_LENGTH)) *
            8.8;
        }
      }
      //if it's not a groupTreeFilter, check if filtermodel has another filter
    } else {
      let filterModelKeys = Object.keys(filterModel).filter(
        (x) => x !== "searchText" && x !== "advancedQuery"
      );
      let nextIndex = filterModelKeys.indexOf(curFilterKey) + 1;
      if (nextIndex < filterModelKeys.length) {
        let chipValue;
        let nextFilterKey = filterModelKeys[nextIndex];
        let nextFilterKeyValues = null;
        switch (filterModel[nextFilterKey].filterType) {
        case GRID_FILTERS.filterType.number:
          nextFilterKeyValues = [getIntFilterVal(filterModel[nextFilterKey])];
          break;
        case GRID_FILTERS.filterType.date:
          nextFilterKeyValues = [
            transformDateChipToText(filterModel[nextFilterKey]),
          ];
          break;
        case GRID_FILTERS.filterType.groupTreeFilter:
          nextFilterKeyValues =
              filterModel[nextFilterKey].columns[
                Object.keys(filterModel[nextFilterKey].columns)[0]
              ].values;
          break;
        case GRID_FILTERS.filterType.set:
        default:
          nextFilterKeyValues = filterModel[nextFilterKey].values;
          break;
        }
        if (nextFilterKeyValues.length > 1) {
          chipValue = nextFilterKeyValues.length;
        } else {
          chipValue =
            filterModel[nextFilterKey].filterType ===
            GRID_FILTERS.filterType.groupTreeFilter
              ? nextFilterKeyValues[0].value
              : nextFilterKeyValues[0];
        }
        //approx. width of filter
        width =
          (filterModel[nextFilterKey].displayName.length +
            (chipValue?.toString().length ?? BLANK_CHIP_LENGTH)) *
          8.8;
      }
    }

    return width;
  };

  const transformChipValue = (displayColumnName, chipValue) => {
    let colDef = columnDefs.find((x) => x.headerName === displayColumnName);

    if (!chipValue)
      return (
        colDef.nullValueFilterLabel ??
        t("components.datagrid.filters.blankValue")
      );

    return colDef?.valueFormatter
      ? colDef.valueFormatter({ value: chipValue })
      : chipValue;
  };

  const clearAllFiltersHandler = () => {
    //clear all filters except those who doesn't exist in the filter panel like global filters (eg. sector at jobs grid)
    let savedFilters = JSON.parse(JSON.stringify(savedLayout.filterModel));
    Object.keys(savedFilters).forEach((x) => {
      if (
        typeof columnDefs.find(
          (y) => y.field === x && (!y.suppressFiltersToolPanel || y.groupFilter)
        ) !== "undefined"
      ) {
        delete savedFilters[x];
      }
    });
    onClearFilters(savedFilters);
  };

  //approx. width of clear button, edit filters btn, and collapsed chips
  let clearBtnWidth = 7;
  let editFiltersBtnWidth = 85;
  let collapsedChipsBtnWidth = 55;
  let items = [];
  let collapsedItemsCount = 0;

  let remainingSpace =
    containerWidth -
    clearBtnWidth -
    collapsedChipsBtnWidth -
    editFiltersBtnWidth;

  _.each(filterModel, (v, k) => {
    let values = [];
    //if filter type is group filter
    if (v.filterType === GRID_FILTERS.filterType.groupTreeFilter) {
      _.each(v.columns, (colVal, colKey) => {
        if (
          typeof columnDefs.find(
            (x) =>
              x.field === colKey &&
              x.suppressFiltersToolPanel &&
              !x.showChips &&
              !x.context?.showChips
          ) === "undefined"
        ) {
          values = colVal.values;
          let filterHasMultipleValues = false;
          let chipValue;
          if (values.length > 1) {
            chipValue = values.length;
            filterHasMultipleValues = true;
          } else {
            chipValue = values[0].value;
          }
          //approx. width of chip to add
          let chipWidth =
            (v.displayName.length +
              (chipValue?.toString().length ?? BLANK_CHIP_LENGTH)) *
            10.5;
          let nexFilterChipWidth = showAll ? 0 : getChipWidthOfNextFilter(k);
          if (
            showAll ||
            chipWidth <= remainingSpace ||
            (nexFilterChipWidth === 0 ||
              chipWidth <=
                remainingSpace + collapsedChipsBtnWidth + nexFilterChipWidth)
          ) {
            remainingSpace = remainingSpace - chipWidth;
            items.push({
              key: k,
              label:
                columnDefs.find((x) => x.field === colKey)?.headerName ??
                v.displayName,
              value: chipValue,
              groupKey: k,
              multiple: filterHasMultipleValues,
              onClick: (e) => expandFilterValues(e, colKey, k),
              // style: getStyles(k),
            });
          } else {
            remainingSpace = 0;
            collapsedItemsCount++;
          }
        }
      });
    } else {
      if (v.filterType === GRID_FILTERS.filterType.number) {
        values = [getIntFilterVal(v)];
      } else if (v.filterType === GRID_FILTERS.filterType.date) {
        values = [getDateFilterVal(v)];
      } else if (v.filterType !== GRID_FILTERS.filterType.groupTreeFilter) {
        values = v.values;
      }

      // Find the column definition whose field property matches the filter model key
      let colDef = columnDefs.find((x) => x.field === k);

      if (!!colDef && colDef.suppressFiltersToolPanel !== true) {
        let filterHasMultipleValues = false;
        let chipValue;
        if (values.length > 1) {
          chipValue = values.length;
          filterHasMultipleValues = true;
        } else {
          chipValue = values[0];
        }
        //approx. width of chip to add
        let chipWidth =
          (v.displayName.length +
            (chipValue?.toString().length ?? BLANK_CHIP_LENGTH)) *
          10.5;
        let nexFilterChipWidth = showAll ? 0 : getChipWidthOfNextFilter(k);
        if (
          showAll ||
          chipWidth <= remainingSpace ||
          (nexFilterChipWidth === 0 ||
            chipWidth <=
              remainingSpace + collapsedChipsBtnWidth + nexFilterChipWidth)
        ) {
          remainingSpace = remainingSpace - chipWidth;
          items.push({
            key: k,
            // If a custom label is provided in the column definition, we use that instead of the filter model value's display name
            label: colDef.filterParams?.customLabel ?? v.displayName,
            value: transformChipValue(v.displayName, chipValue),
            multiple: filterHasMultipleValues,
            onClick: (e) => expandFilterValues(e, k),
            // style: getStyles(k),
          });
        } else {
          remainingSpace = 0;
          collapsedItemsCount++;
        }
      }
    }
  });

  //if should dipslay number of collapsed items
  if (collapsedItemsCount > 0) {
    items.push({
      key: "collapsedItemsCount",
      label: `+${collapsedItemsCount}`,
      onClick: () => setShowAll(true),
    });
  }

  return (
    <>
      <div ref={containerRef}>
        <SFChipList
          items={items}
          variant="filterChip"
          customActions={
            <ActionBtns
              readOnly={readOnly}
              {...(showAll && { onShowLess: () => setShowAll(false) })}
              onEditFilters={onEditFilters}
              onDeleteAll={onClearFilters && clearAllFiltersHandler}
            />
          }
        />
      </div>
      <SFPopoverStyled
        anchorEl={anchorEl}
        position="bottom-start"
        onClose={() => setAnchorEl(null)}
      >
        <SFStack>
          {popoverItems.map((x, index) => (
            <SFChip
              key={index}
              size="small"
              variant="popoverFilterChip"
              {...(!readOnly && {
                onDelete: () =>
                  removeFilter(x.key, x.value, x.filterType, x.groupKey, x.id),
                deleteIcon: <SFCloseIcon />,
              })}
            >
              <SFTypography variant="textLabel">
                {x.displayValue ?? x.value}
              </SFTypography>
            </SFChip>
          ))}
        </SFStack>
      </SFPopoverStyled>
    </>
  );
};

FilterChips.propTypes = {
  gridName: PropTypes.string.isRequired,
  onClearFilters: PropTypes.func,
  onEditFilters: PropTypes.func,
  getAcceptableFilters: PropTypes.func,
  columnDefs: PropTypes.array.isRequired,
};

export default FilterChips;
